import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import Layout from "../components/layout/Layout";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { HQ_LEGAL } from "../lib/mansionLegalUrls";

export default function RegisterPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!acceptedTerms) {
      toast.error("You must accept the Terms of Service and Privacy Policy to register.");
      return;
    }

    setSubmitting(true);
    try {
      const { data } = await axios.post(
        `${API}/auth/register`,
        { name, email, password, acceptedTerms: true, userAgreement: true },
        { withCredentials: true }
      );
      login(data, data.token);
      toast.success("Account created successfully");
      navigate("/");
    } catch (error) {
      const message = error?.response?.data?.error || error?.response?.data?.message || "Registration failed";
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout>
      <div className="container mx-auto px-4 py-12 max-w-md">
        {/* This page sits on the light Layout shell, so the heading was
            white-on-white and the body copy barely readable. */}
        <h1 className="text-2xl font-bold text-slate-900 mb-2">
          Create your CheepSweepz account
        </h1>
        <p className="text-slate-600 mb-8 text-sm">
          Register to track imports, orders, and your personal catalog.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="name">Full name</Label>
            <Input id="name" value={name} onChange={(e) => setName(e.target.value)} required />
          </div>
          <div>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div>
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              minLength={8}
              required
            />
          </div>

          {/* slate-300 on white is ~1.5:1 — this is consent copy that has to be
              readable before the box is ticked. */}
          <label className="flex items-start gap-3 text-sm text-slate-700 cursor-pointer">
            <input
              type="checkbox"
              className="mt-1"
              checked={acceptedTerms}
              onChange={(e) => setAcceptedTerms(e.target.checked)}
              required
            />
            <span>
              I agree to the{" "}
              <a href={HQ_LEGAL.terms} target="_blank" rel="noopener noreferrer" className="text-orange-700 underline">
                Terms of Service
              </a>
              ,{" "}
              <a href={HQ_LEGAL.privacy} target="_blank" rel="noopener noreferrer" className="text-orange-700 underline">
                Privacy Policy
              </a>
              , and{" "}
              <a href={HQ_LEGAL.cookies} target="_blank" rel="noopener noreferrer" className="text-orange-700 underline">
                Cookie Policy
              </a>{" "}
              hosted by Digital Hustlers Caribbean.
            </span>
          </label>

          {/* Explicit colours rather than the themed default: this is the primary
              conversion button and must never depend on a token resolving. */}
          <Button
            type="submit"
            disabled={submitting || !acceptedTerms}
            className="w-full py-3 px-4 bg-orange-600 hover:bg-orange-700 text-white font-bold text-base rounded-lg shadow-md h-auto transition-colors focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-2 disabled:opacity-100 disabled:bg-slate-200 disabled:text-slate-600 disabled:cursor-not-allowed"
            data-testid="register-submit"
          >
            {submitting ? "Creating account…" : "Create account"}
          </Button>
          {!acceptedTerms && (
            <p className="text-xs text-slate-500 text-center">
              Accept the terms above to enable account creation.
            </p>
          )}
        </form>

        <p className="text-slate-500 text-sm mt-6 text-center">
          Already have an account?{" "}
          <Link to="/login" className="font-medium text-orange-700 hover:underline">
            Sign in
          </Link>
        </p>
      </div>
    </Layout>
  );
}
